/**
 * Normaliza um CPF para 11 dígitos (somente números).
 *
 * Trata o caso clássico do Excel: quando o CPF começa com zero, o Excel o
 * interpreta como número e remove os zeros à esquerda (ex: "012.345.678-90"
 * vira o número 1234567890). Aqui, qualquer valor com menos de 11 dígitos é
 * completado com zeros à esquerda, recuperando o CPF original.
 *
 * @param {string|number} valor CPF com ou sem máscara.
 * @returns {string} CPF com 11 dígitos (ou só os dígitos, se exceder 11).
 */
export function normalizarCpf(valor) {
  if (valor == null) return '';
  const digitos = String(valor).replace(/\D/g, '');
  // Completa com zeros à esquerda até 11 dígitos (recupera zeros perdidos pelo Excel)
  return digitos.length < 11 ? digitos.padStart(11, '0') : digitos;
}

/**
 * Valida um CPF brasileiro usando os dígitos verificadores.
 * Aceita CPF com ou sem máscara (pontos e traço) e recupera zeros à esquerda
 * perdidos pelo Excel.
 *
 * @param {string|number} valor CPF a ser validado.
 * @returns {boolean} true se o CPF for válido.
 */
export function validarCpf(valor) {
  if (valor == null) return false;

  // Normaliza, recuperando zeros à esquerda que o Excel possa ter removido
  const cpf = normalizarCpf(valor);

  // Precisa ter exatamente 11 dígitos
  if (cpf.length !== 11) return false;

  // Rejeita sequências repetidas (000.000.000-00, 111..., etc.)
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  // Calcula o dígito verificador a partir dos primeiros `tamanho` dígitos
  const calcularDigito = (tamanho) => {
    let soma = 0;
    let peso = tamanho + 1;
    for (let i = 0; i < tamanho; i++) {
      soma += parseInt(cpf.charAt(i), 10) * peso;
      peso--;
    }
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };

  const digito1 = calcularDigito(9);
  if (digito1 !== parseInt(cpf.charAt(9), 10)) return false;

  const digito2 = calcularDigito(10);
  if (digito2 !== parseInt(cpf.charAt(10), 10)) return false;

  return true;
}

export default validarCpf;
